/**
 * Dashboard telemetry client — SPA-side `page_views` ingest.
 *
 * Two primitives that mirror `src/server/routes/api/page_views_ingest.rs`:
 *
 *   * {@link useTrackPageView} — React hook that watches
 *     `useLocation()` and POSTs to `/api/page-views/ping` on every
 *     client-side route change. Mounted once at the top-level
 *     `App` component root so every navigation is captured,
 *     including same-route prop changes, back-button rehydrates
 *     from TanStack Query cache, and transitions that don't
 *     trigger any data fetch.
 *   * {@link trackInteraction} — imperative helper that POSTs to
 *     `/api/page-views/interaction` with a structured metadata
 *     payload describing a sort / filter / window / row-click
 *     event. Called from component event handlers.
 *
 * # 2026-05-01 operator directive
 *
 * `/admin/page-views` must capture "every single route in the app,
 * every time something is loaded, every user" + eventually
 * "tracking of sorting, filtering etc on pages so we can analyze
 * usage." The Axum `page_view_middleware` (see
 * `src/server/middleware.rs`) already records every authenticated
 * `/api/*` GET — that catches SPA page loads that fetch data.
 * This module catches the remaining SPA navigations (cache-hit
 * rehydrates, back-button, same-route-prop-change) AND adds the
 * Tier 2 interaction layer the operator asked for.
 *
 * # Fail-open posture
 *
 * Every call is fire-and-forget:
 *   * `fetch(...)` is NOT awaited at the call site
 *   * `.catch(() => {})` swallows network errors
 *   * On 401 the SPA's OAuth flow takes over on the NEXT API call
 *     anyway (the session-expired user gets bounced to login)
 *   * Server-side, the `PAGE_VIEW_RECORD_ERRORS` counter tracks
 *     drops + the `page_view_record_exhausted` structured log
 *     event names them
 *
 * No retry on the client side — if the POST fails, the event is
 * lost. Acceptable for a telemetry path where duplicates would
 * inflate counts more than drops would deflate them.
 *
 * # Dedup
 *
 * `useTrackPageView` dedups on unchanged pathname+search to avoid
 * same-location re-renders firing redundant pings. Interaction
 * events are NOT deduped — two rapid filter toggles produce two
 * rows, which is the correct semantic ("the user toggled twice").
 *
 * # Privacy
 *
 * Users are private cantina.ai employees (per the 2026-05-01
 * operator directive); no consent notice required. The client
 * sends only the route + interaction context; session identity
 * is derived server-side from the OAuth cookie, so the client
 * cannot spoof attribution.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

// ─── Page-view ping hook ────────────────────────────────────────────────────

/**
 * React hook that POSTs to `/api/page-views/ping` on every
 * client-side route change.
 *
 * Mount once at the top-level `App` component inside
 * `<BrowserRouter>` (because `useLocation` requires the router
 * context). On every distinct `pathname + search` change, fire a
 * fire-and-forget POST with the route as the body.
 *
 * # Dedup
 *
 * The hook tracks the previously-recorded location in a ref so
 * same-location re-renders (parent state change, React strict-
 * mode double-invoke, TanStack Query refetch triggering a
 * re-render) DON'T produce redundant pings. A genuine
 * navigation back to the same path (rare but possible via
 * `navigate(same_path)`) would also be deduped — acceptable
 * because the operator's mental model is "I'm on this page"
 * regardless of how many times React re-rendered it.
 *
 * # First render
 *
 * The hook fires on the initial mount (when `prev.current` is
 * `null`). This captures the entry-point page load — the very
 * first route the user lands on after OAuth or a direct bookmark
 * open.
 *
 * # React 19 StrictMode double-invoke
 *
 * React 19's `<StrictMode>` in dev mode invokes effects twice to
 * surface impurity bugs. The `prev.current` dedup naturally
 * absorbs the second invocation because the first invocation
 * already set the ref to the current location. No additional
 * guard needed.
 */
export function useTrackPageView(): void {
  const location = useLocation();
  const prev = useRef<string | null>(null);

  useEffect(() => {
    const current = location.pathname + location.search;
    if (current === prev.current) {
      return;
    }
    prev.current = current;

    // Fire-and-forget. The `.catch` prevents unhandled-rejection
    // warnings in the console when the network fails or the
    // server returns 401/503. Server-side counters + structured
    // logs catch the real-signal drops.
    void fetch("/api/page-views/ping", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ route: current }),
    }).catch(() => {
      // Intentional no-op — see module doc.
    });
  }, [location.pathname, location.search]);
}

// ─── Interaction tracking ───────────────────────────────────────────────────

/**
 * One of the four interaction kinds accepted by
 * `POST /api/page-views/interaction` (see the backend's
 * `parse_interaction_action_kind` for the canonical list).
 *
 * Kept as a narrow union rather than a free-form string so a typo
 * in a call site fails TypeScript-check before shipping.
 */
export type InteractionKind =
  | "sort-changed"
  | "filter-changed"
  | "window-changed"
  | "row-clicked";

/**
 * Metadata shape for `sort-changed` events. Matches the server's
 * `validate_interaction_metadata` expectations for
 * `ActionKind::SortChanged`.
 */
export interface SortChangedMetadata {
  page: string;
  to_column: string;
  to_direction: "asc" | "desc";
  from_column?: string;
}

/**
 * Metadata shape for `filter-changed` events. `filter_values_after`
 * is optional — sending only `filter_key` identifies which filter
 * the user touched without enumerating the post-change selection
 * (useful for sliders or multi-chip controls that settle over
 * many intermediate values).
 */
export interface FilterChangedMetadata {
  page: string;
  filter_key: string;
  filter_values_after?: string[];
  from_values?: string[];
}

/**
 * Metadata shape for `window-changed` events — the time-window
 * selector on `/trends` (24h / 7d / 30d) and similar controls.
 */
export interface WindowChangedMetadata {
  page: string;
  to_window: string;
  from_window?: string;
}

/**
 * Metadata shape for `row-clicked` events — drill-through from a
 * list page into a detail surface.
 */
export interface RowClickedMetadata {
  page: string;
  target_kind: string;
  target_id?: string;
  rank_on_page?: number;
}

/**
 * Union of every metadata shape — lets `trackInteraction` accept
 * a typed payload per kind at the call site instead of a
 * free-form `Record<string, unknown>`. A mismatched pair (e.g.
 * `trackInteraction("sort-changed", { filter_key: "x" })`) fails
 * TypeScript-check before shipping.
 */
export type InteractionMetadata =
  | ({ kind: "sort-changed" } & SortChangedMetadata)
  | ({ kind: "filter-changed" } & FilterChangedMetadata)
  | ({ kind: "window-changed" } & WindowChangedMetadata)
  | ({ kind: "row-clicked" } & RowClickedMetadata);

/**
 * Hard cap on the serialized metadata payload. Mirrors the
 * backend's `METADATA_MAX_BYTES = 4096`. If the caller's metadata
 * serializes to more than this, we drop the event client-side
 * with a console.warn — the server would reject with 400 anyway,
 * but catching client-side avoids the wasted round-trip.
 */
const METADATA_MAX_BYTES = 4096;

/**
 * POST an interaction event to `/api/page-views/interaction`.
 *
 * Fire-and-forget. The handler is typed so the call site can't
 * accidentally mix metadata shapes; the `kind` discriminator on
 * the metadata must match the first argument.
 *
 * # Typical call sites
 *
 * ```tsx
 * // Sort column change on /trends
 * trackInteraction({
 *   kind: "sort-changed",
 *   page: "/trends",
 *   from_column: "total_count",
 *   to_column: "last_seen",
 *   to_direction: "desc",
 * });
 *
 * // Filter chip toggle on /bundles
 * trackInteraction({
 *   kind: "filter-changed",
 *   page: "/bundles",
 *   filter_key: "platform",
 *   filter_values_after: ["ios"],
 * });
 *
 * // Time-window selector on /trends
 * trackInteraction({
 *   kind: "window-changed",
 *   page: "/trends",
 *   from_window: "30d",
 *   to_window: "7d",
 * });
 *
 * // Row click (drill-through)
 * trackInteraction({
 *   kind: "row-clicked",
 *   page: "/trends",
 *   target_kind: "duplicates",
 *   target_id: "3a0ebcc4-6b4c-4c4d-9d2d-5fd7e1b4b9d1",
 *   rank_on_page: 3,
 * });
 * ```
 *
 * # Fail-open
 *
 * Every failure mode (serialization oversize, network error, 4xx,
 * 5xx) is swallowed silently. Server-side counters track drops.
 * The operator's flow on the page is NEVER blocked by a failed
 * telemetry write.
 */
export function trackInteraction(event: InteractionMetadata): void {
  const { kind, ...metadata } = event;

  // Client-side size gate. The server enforces the same limit
  // (with a 400 INVALID_METADATA response) but catching here
  // avoids the round-trip for obviously-oversized payloads.
  let body: string;
  try {
    body = JSON.stringify({
      action_kind: kind,
      metadata,
      route: metadata.page,
    });
  } catch {
    // JSON.stringify throws on circular references or BigInts.
    // Shouldn't happen for our typed shapes, but belt-and-suspenders.
    return;
  }
  if (new Blob([body]).size > METADATA_MAX_BYTES) {
    // In a dev build the console.warn surfaces the oversize to a
    // contributor who's adding a new interaction kind with runaway
    // metadata. In production this is silent (console.warn is
    // ignored by the audit trail).
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        `[tracking] interaction metadata exceeds ${METADATA_MAX_BYTES}-byte limit; dropping event`,
        { kind, size: new Blob([body]).size },
      );
    }
    return;
  }

  void fetch("/api/page-views/interaction", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body,
  }).catch(() => {
    // Intentional no-op — see module doc.
  });
}

// ─── Convenience narrow-typed wrappers ──────────────────────────────────────
//
// These wrap `trackInteraction` with per-kind signatures so the
// caller doesn't have to repeat the `kind` field. Slight verbosity
// win at the call site + a bit tighter typing when the kind is
// literal at the invocation site.

/** Record a sort-column change. See {@link SortChangedMetadata}. */
export function trackSortChange(metadata: SortChangedMetadata): void {
  trackInteraction({ kind: "sort-changed", ...metadata });
}

/** Record a filter toggle / selection change. */
export function trackFilterChange(metadata: FilterChangedMetadata): void {
  trackInteraction({ kind: "filter-changed", ...metadata });
}

/** Record a time-window selector change. */
export function trackWindowChange(metadata: WindowChangedMetadata): void {
  trackInteraction({ kind: "window-changed", ...metadata });
}

/** Record a row-click drill-through. */
export function trackRowClick(metadata: RowClickedMetadata): void {
  trackInteraction({ kind: "row-clicked", ...metadata });
}
